import { Link, useParams, useSearchParams } from "react-router-dom";
import { CalendarClock, User } from "lucide-react";
import Seo from "@/components/site/Seo";
import Markdown from "@/components/site/Markdown";
import SoftwareCard from "@/components/site/SoftwareCard";
import { PageHeader, Loading, Empty, Pagination, ContentCard, articleToItem, AffiliateNotice } from "@/components/site/Ui";
import { useApi, BLOG_CATEGORIES, formatDate, fileUrl } from "@/lib/api";

export function BlogPage() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("category") || "";
  const page = Number(params.get("page") || 1);
  const { data, loading } = useApi(`/articles?blog_category=${encodeURIComponent(cat)}&page=${page}&limit=9`);
  const go = (c, p = 1) => { const n = new URLSearchParams(); if (c) n.set("category", c); if (p > 1) n.set("page", p); setParams(n); };
  return (
    <>
      <Seo title={cat ? `${cat} — Blog` : "Blog"} description="SaaS guides, software reviews, business technology, productivity, AI tools and startup resources." />
      <PageHeader eyebrow="Blog" title="Insights for better software decisions" testId="blog-page-header">Guides, reviews and resources for evaluating business technology.</PageHeader>
      <div className="container-x py-12">
        <div className="flex flex-wrap gap-2 mb-10" role="tablist" aria-label="Blog categories">
          {["", ...BLOG_CATEGORIES].map((c) => (
            <button key={c || "all"} role="tab" aria-selected={cat === c} onClick={() => go(c)} data-testid={`blog-cat-${c ? c.toLowerCase().replace(/\s+/g, "-") : "all"}`}
              className={`btn btn-sm border ${cat === c ? "bg-[#17243A] text-white border-[#17243A]" : "bg-white border-[#CBD6DB] text-[#17243A] hover:border-[#3F5E7A]"}`}>{c || "All"}</button>
          ))}
        </div>
        {loading ? <Loading /> : !data?.items.length ? <Empty title="No articles in this category yet" /> : (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{data.items.map((a) => <ContentCard key={a.slug} item={articleToItem(a)} testId={`article-card-${a.slug}`} />)}</div>
            <Pagination page={data.page} pages={data.pages} onChange={(p) => go(cat, p)} />
          </>
        )}
      </div>
    </>
  );
}

export function ArticleDetail() {
  const { slug } = useParams();
  const { data: a, loading, error } = useApi(`/articles/${slug}`);
  if (loading) return <Loading />;
  if (error) return <div className="container-x py-20"><Empty title="Article not found"><Link to="/blog" className="text-[#3F5E7A] underline">Back to the blog</Link></Empty></div>;
  return (
    <article data-testid="article-page">
      <Seo title={a.seo_title || a.title} description={a.seo_description || a.excerpt} type="article" image={fileUrl(a.cover_image)}
        jsonLd={{ "@context": "https://schema.org", "@type": "Article", headline: a.title, description: a.excerpt, datePublished: a.published_at, dateModified: a.updated_at,
          author: { "@type": "Organization", name: a.author }, publisher: { "@type": "Organization", name: "SaaSPivora" } }} />
      <header className="container-x max-w-4xl pt-14 pb-8 rise">
        <Link to={`/blog?category=${encodeURIComponent(a.blog_category)}`} className="eyebrow hover:underline">{a.blog_category}</Link>
        <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold leading-tight" data-testid="article-title">{a.title}</h1>
        {a.excerpt && <p className="mt-4 text-lg">{a.excerpt}</p>}
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#64728A]" data-testid="article-meta">
          <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" />{a.author}</span>
          <span className="flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" />Published {formatDate(a.published_at)}</span>
          <span>Updated {formatDate(a.updated_at)}</span>
        </div>
      </header>
      {a.cover_image && (
        <figure className="container-x max-w-5xl" data-testid="article-cover">
          <img src={fileUrl(a.cover_image)} alt={a.cover_alt || ""} className="w-full rounded-2xl max-h-[460px] object-cover" />
          {a.cover_credit && (
            <figcaption className="mt-2 text-xs text-[#64728A]" data-testid="article-cover-credit">
              {a.cover_credit_url ? <a href={a.cover_credit_url} target="_blank" rel="noopener noreferrer" className="hover:underline">{a.cover_credit}</a> : a.cover_credit}
            </figcaption>
          )}
        </figure>
      )}
      <div className="container-x max-w-3xl py-10">
        {a.has_affiliate_links && <div className="mb-8"><AffiliateNotice /></div>}
        <Markdown className="text-[17px]">{a.content}</Markdown>
      </div>
      {a.software_docs?.length > 0 && (
        <section className="container-x py-10"><h2 className="text-2xl font-bold mb-6">Software mentioned</h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{a.software_docs.map((s) => <SoftwareCard key={s.slug} s={s} />)}</div></section>
      )}
      {a.more?.length > 0 && (
        <section className="container-x py-10"><h2 className="text-2xl font-bold mb-6">More from the blog</h2>
          <div className="grid gap-6 md:grid-cols-3">{a.more.map((m) => <ContentCard key={m.slug} item={articleToItem(m)} testId={`more-article-${m.slug}`} />)}</div></section>
      )}
    </article>
  );
}
